import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => {
      clearInterval(t);
      document.removeEventListener("mousedown", onClick);
    };
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/admin/notifications/mark-all-read");
      load();
    } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="btn-brut btn-ghost relative"
        data-testid="admin-notif-bell"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="admin-notif-count"
            className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 border-2 border-black bg-[rgb(var(--flame))] grid place-items-center font-mono text-[10px] font-bold px-1"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="admin-notif-panel"
          className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] overflow-auto brut-card bg-[rgb(var(--paper))] z-40"
        >
          <div className="p-3 border-b-2 border-black flex items-center justify-between sticky top-0 bg-[rgb(var(--paper))]">
            <div className="font-mono text-[11px] uppercase tracking-widest">
              Notifications
            </div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                data-testid="admin-notif-mark-all"
                className="btn-brut btn-ghost text-[10px]"
              >
                <CheckCheck size={11} /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center font-mono text-xs text-black/60">
              No notifications
            </div>
          ) : (
            <ul>
              {items.map((n) => (
                <li
                  key={n.id}
                  data-testid={`admin-notif-${n.id}`}
                  className={`p-3 border-b-2 border-black ${n.read ? "" : "bg-[rgb(var(--paper-2))]"}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && (
                      <span className="mt-1.5 w-2 h-2 bg-[rgb(var(--flame))] flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm">{n.title}</div>
                      <div className="text-xs text-black/70 mt-1">{n.body}</div>
                      {n.candidate_id && (
                        <Link
                          to={`/admin/candidates/${n.candidate_id}`}
                          className="btn-brut btn-ghost text-[10px] mt-2"
                          onClick={() => setOpen(false)}
                        >
                          Open candidate →
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
